import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const toISO = (date) => date.toLocaleDateString("en-CA");

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
};

const endOfWeek = (date) => {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  return d;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);
const endOfYear = (date) => new Date(date.getFullYear(), 11, 31);

const buildPresets = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeekAnchor = new Date(today);
  lastWeekAnchor.setDate(lastWeekAnchor.getDate() - 7);
  const lastMonthAnchor = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastYearAnchor = new Date(today.getFullYear() - 1, 0, 1);

  return [
    { label: "Today", range: () => [today, today] },
    { label: "Yesterday", range: () => [yesterday, yesterday] },
    { label: "This Week", range: () => [startOfWeek(today), endOfWeek(today)] },
    { label: "Last Week", range: () => [startOfWeek(lastWeekAnchor), endOfWeek(lastWeekAnchor)] },
    { label: "This Month", range: () => [startOfMonth(today), endOfMonth(today)] },
    { label: "Last Month", range: () => [startOfMonth(lastMonthAnchor), endOfMonth(lastMonthAnchor)] },
    { label: "This Year", range: () => [startOfYear(today), endOfYear(today)] },
    { label: "Last Year", range: () => [startOfYear(lastYearAnchor), endOfYear(lastYearAnchor)] },
  ];
};

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const MonthGrid = ({ month, year, rangeStart, rangeEnd, hoverDate, onDayClick, onDayHover }) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const effectiveEnd = rangeEnd || hoverDate;

  const isInRange = (day) => {
    if (!day || !rangeStart || !effectiveEnd) return false;
    const d = new Date(year, month, day);
    const lo = rangeStart < effectiveEnd ? rangeStart : effectiveEnd;
    const hi = rangeStart < effectiveEnd ? effectiveEnd : rangeStart;
    return (
      d >= new Date(lo.getFullYear(), lo.getMonth(), lo.getDate()) &&
      d <= new Date(hi.getFullYear(), hi.getMonth(), hi.getDate())
    );
  };

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            className="w-7 h-5 flex items-center justify-center text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase select-none"
          >
            {wd}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} className="w-7 h-7" />;

          const date = new Date(year, month, day);
          const inRange = isInRange(day);
          const isStart = isSameDay(date, rangeStart);
          const isEnd = isSameDay(date, rangeEnd);
          const isTodayCell = isSameDay(date, today);

          return (
            <button
              key={idx}
              type="button"
              onClick={() => onDayClick(date)}
              onMouseEnter={() => onDayHover(date)}
              className={`w-7 h-7 text-xs font-medium rounded-md transition-colors duration-100 flex items-center justify-center ${
                isStart || isEnd
                  ? "bg-blue-600 text-white font-semibold shadow-xs"
                  : inRange
                  ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                  : isTodayCell
                  ? "text-blue-600 dark:text-blue-400 font-bold border border-blue-400 dark:border-blue-500 hover:bg-blue-50 dark:hover:bg-slate-700"
                  : "text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateRangePicker = ({
  startValue,
  endValue,
  initialStart,
  initialEnd,
  onChange,
  onClose,
  className,
}) => {
  const pickerRef = useRef(null);
  const start = startValue || initialStart;
  const end = endValue || initialEnd;
  const initialAnchor = start ? new Date(start) : new Date();
  const [viewYear, setViewYear] = useState(initialAnchor.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialAnchor.getMonth());
  const [rangeStart, setRangeStart] = useState(start ? new Date(start) : null);
  const [rangeEnd, setRangeEnd] = useState(end ? new Date(end) : null);
  const [hoverDate, setHoverDate] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const presets = buildPresets();

  const handlePresetClick = (preset) => {
    const [pStart, pEnd] = preset.range();
    setRangeStart(pStart);
    setRangeEnd(pEnd);
    setViewMonth(pStart.getMonth());
    setViewYear(pStart.getFullYear());
    onChange({ start: toISO(pStart), end: toISO(pEnd) });
    onClose?.();
  };

  const handleDayClick = (date) => {
    if (!rangeStart || rangeEnd) {
      setRangeStart(date);
      setRangeEnd(null);
      return;
    }
    const dStart = date < rangeStart ? date : rangeStart;
    const dEnd = date < rangeStart ? rangeStart : date;
    setRangeStart(dStart);
    setRangeEnd(dEnd);
    onChange({ start: toISO(dStart), end: toISO(dEnd) });
    onClose?.();
  };

  const shiftMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  const monthLabel = new Date(viewYear, viewMonth).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div
      ref={pickerRef}
      className={
        className ||
        "absolute bottom-full left-0 mb-2 z-50 flex bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      }
    >
      {/* Presets */}
      <div className="w-28 shrink-0 border-r border-gray-200 dark:border-slate-700 p-2 space-y-1 bg-gray-50/50 dark:bg-slate-900/40">
        <div className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase px-1.5 pb-1">
          Presets
        </div>
        {presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => handlePresetClick(preset)}
            className="w-full text-left px-2 py-1 rounded-md text-xs font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-200/60 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Calendar */}
      <div className="p-3 min-w-[240px]" onMouseLeave={() => setHoverDate(null)}>
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-300 transition-colors"
            title="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h4 className="text-xs font-semibold text-gray-900 dark:text-white whitespace-nowrap px-1">
            {monthLabel}
          </h4>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-300 transition-colors"
            title="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <MonthGrid
          month={viewMonth}
          year={viewYear}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          hoverDate={hoverDate}
          onDayClick={handleDayClick}
          onDayHover={setHoverDate}
        />
      </div>
    </div>
  );
};

export default DateRangePicker;
